import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface MenuItem {
  path: string;
  icon: string;
  label: string;
  badge?: number;
  submenu?: MenuItem[];
  permission?: string;
  adminOnly?: boolean; 
}

interface SidebarNewProps {
  onToggle?: (isOpen: boolean) => void;
}

function SidebarNew({ onToggle }: SidebarNewProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});
  const [popupMenu, setPopupMenu] = useState<string | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // تحميل حالة السايد بار من localStorage عند بدء التشغيل
  useEffect(() => {
    const savedState = localStorage.getItem('sidebar_open');
    if (savedState !== null) {
      setIsOpen(savedState === 'true');
    }
    
    const userJson = sessionStorage.getItem('user');
    if (userJson) {
      setUser(JSON.parse(userJson));
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setPopupMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    localStorage.setItem('sidebar_open', String(newState));
    if (onToggle) {
      onToggle(newState);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('user');
    navigate('/login');
  };

  const toggleMenu = (menuLabel: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuLabel]: !prev[menuLabel]
    }));
  };

  // دالة التحقق من الصلاحية
  const hasPermission = (permission?: string, adminOnly?: boolean): boolean => {
  if (!user) return false;
  if (adminOnly) return user.role === 'admin';
  if (user.role === 'admin') return true;
  if (!permission) return true;
  return user[permission] === true;
};

  const isAdmin = user?.role === 'admin';

  // قائمة كل العناصر (هيتم تصفيتها حسب الصلاحيات)
const allMenuItems: MenuItem[] = [
  { path: '/', icon: '🏪', label: 'نقطة البيع', permission: 'can_create_sale' },
  { path: '/tables', icon: '🪑', label: 'الترابيزات', permission: 'can_view_tables' },
  { path: '/returns', icon: '↩️', label: 'المرتجعات', permission: 'can_view_returns' },
  { path: '/dashboard', icon: '📊', label: 'لوحة التحكم', permission: 'can_view_dashboard' },
  { path: '/sales', icon: '💰', label: 'فواتير البيع', permission: 'can_view_sales' },
  {
  path: '#',
  icon: '👥',
  label: 'العملاء',
  permission: 'can_view_customers',
  submenu: [
    { path: '/customers', icon: '📋', label: 'قائمة العملاء', permission: 'can_view_customers' },
    { path: '/crm', icon: '📊', label: 'تحليلات العملاء', permission: 'can_view_reports' },
    { path: '/credit-customers', icon: '💰', label: 'عملاء الأجل', permission: 'can_view_customers' }
  ]
},
  {
    path: '#',
    icon: '📦',
    label: 'المخزون',
    permission: 'can_view_products',
    submenu: [
      { path: '/admin', icon: '📋', label: 'إدارة الأصناف', permission: 'can_view_products' },
      { path: '/categories', icon: '🏷️', label: 'التصنيفات', permission: 'can_view_categories' },
      { path: '/movements', icon: '🔄', label: 'حركة المنتجات', permission: 'can_view_movements' },
      { path: '/inventory-reports', icon: '📊', label: 'تقارير المخزون', permission: 'can_view_reports' }
    ]
  },
  {
    path: '#',
    icon: '🧪',
    label: 'المواد والوصفات',
    permission: 'can_view_raw_materials',
    submenu: [
      { path: '/raw-materials', icon: '📦', label: 'المواد الخام', permission: 'can_view_raw_materials' },
      { path: '/recipes', icon: '📝', label: 'الوصفات', permission: 'can_view_recipes' }
    ]
  },
  {
    path: '#',
    icon: '🤝',
    label: 'الموردين',
    permission: 'can_view_suppliers',
    submenu: [
      { path: '/suppliers', icon: '📋', label: 'قائمة الموردين', permission: 'can_view_suppliers' },
      { path: '/supplier-stats', icon: '📊', label: 'إحصائيات الموردين', permission: 'can_view_reports' }
    ]
  },
  {
    path: '#',
    icon: '📄',
    label: 'فواتير الشراء',
    permission: 'can_view_invoices',
    submenu: [
      { path: '/invoices', icon: '📋', label: 'كل الفواتير', permission: 'can_view_invoices' }
    ]
  },
  {
    path: '#',
    icon: '💸',
    label: 'المالية',
    permission: 'can_view_financial',
    submenu: [
      { path: '/expenses', icon: '📉', label: 'المصروفات', permission: 'can_view_expenses' },
      { path: '/salaries', icon: '👥', label: 'المرتبات', permission: 'can_view_salaries' },
      { path: '/assets', icon: '🏢', label: 'الأصول الثابتة', permission: 'can_view_assets' },
      { path: '/cash-management', icon: '💵', label: 'إدارة الخزنة', permission: 'can_view_financial' }
    ]
  },
  {
    path: '#',
    icon: '📅',
    label: 'الطلبيات',
    permission: 'can_view_orders',
    submenu: [
      { path: '/orders', icon: '📦', label: 'كل الطلبيات', permission: 'can_view_orders' },
      { path: '/orders/new', icon: '➕', label: 'طلبية جديدة', permission: 'can_create_order' }
    ]
  },
  {
    path: '#',
    icon: '🔄',
    label: 'إعادة الطلب',
    permission: 'can_view_orders',
    submenu: [
      { path: '/reorder', icon: '📋', label: 'الطلبات السابقة', permission: 'can_view_orders' }
    ]
  },
  {
    path: '#',
    icon: '👥',
    label: 'الموظفين',
    permission: 'can_view_users',
    submenu: [
      { path: '/employee-performance', icon: '📊', label: 'تقارير الأداء', permission: 'can_view_reports' }
    ]
  },
  {
    path: '#',
    icon: '💰',
    label: 'التقارير',
    permission: 'can_view_reports',
    submenu: [
      { path: '/financial-reports', icon: '📈', label: 'التقارير المالية', permission: 'can_view_reports' },
      { path: '/tax-reports', icon: '🧾', label: 'التقارير الضريبية', permission: 'can_view_reports' }
    ]
  },
  { path: '/logs', icon: '📜', label: 'سجل التعديلات', adminOnly: true },
  { path: '/settings', icon: '⚙️', label: 'الإعدادات', permission: 'can_view_settings' }
];

  // دالة لتصفية القائمة حسب الصلاحيات
  const filterMenuByPermissions = (items: MenuItem[]): MenuItem[] => {
    return items
      .filter(item => {
  // تحقق من صلاحية العنصر الرئيسي (مع adminOnly)
  return hasPermission(item.permission, item.adminOnly);
})
      .map(item => {
        if (item.submenu) {
          // تصفية القائمة الفرعية
          const filteredSubmenu = item.submenu.filter(sub => hasPermission(sub.permission, sub.adminOnly));
          if (filteredSubmenu.length === 0) {
            return null; // لو القائمة الفرعية فاضية، متظهرش القائمة الرئيسية
          }
          return { ...item, submenu: filteredSubmenu };
        }
        return item;
      })
      .filter(Boolean) as MenuItem[];
  };

  // الحصول على القائمة المصفاة حسب صلاحيات المستخدم
  const menuItems = filterMenuByPermissions(allMenuItems);

  const sidebarStyle = {
    position: 'fixed' as const,
    top: 0,
    right: 0,
    height: '100vh',
    backgroundColor: '#2c3e50',
    color: 'white',
    transition: 'width 0.3s',
    width: isOpen ? '250px' : '60px',
    zIndex: 1000,
    boxShadow: '-2px 0 10px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column' as const
  };

  const menuContainerStyle = {
    flex: 1,
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    padding: '10px 0'
  };

  const toggleButtonStyle = {
    width: '100%',
    padding: '15px',
    backgroundColor: '#34495e',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    fontSize: '20px',
    textAlign: 'center' as const,
    borderBottom: '2px solid #4CAF50'
  };

  const menuItemStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 15px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    borderBottom: '1px solid #34495e',
    color: 'white',
    textDecoration: 'none'
  };

  const iconStyle = {
    fontSize: '20px',
    minWidth: '40px',
    textAlign: 'center' as const
  };

  const labelStyle = {
    marginRight: '10px',
    opacity: isOpen ? 1 : 0,
    transition: 'opacity 0.2s',
    whiteSpace: 'nowrap' as const,
    flex: 1
  };

  const arrowStyle = {
    marginRight: '10px',
    fontSize: '12px',
    opacity: isOpen ? 1 : 0,
    transition: 'transform 0.2s'
  };

  const logoutButtonStyle = {
    margin: '15px',
    padding: '12px',
    backgroundColor: '#f44336',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    display: isOpen ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontSize: '14px',
    fontWeight: 'bold' as const
  };

  const renderMenuItem = (item: MenuItem, index: number) => {
    const hasSubmenu = item.submenu && item.submenu.length > 0;

    if (!isOpen) {
      return (
        <div key={index} style={{ position: 'relative' }}>
          <div
            onClick={() => {
              if (hasSubmenu) {
                setPopupMenu(popupMenu === item.label ? null : item.label);
              } else {
                navigate(item.path);
              }
            }}
            style={menuItemStyle}
            title={item.label}
          >
            <span style={iconStyle}>{item.icon}</span>
          </div>
          
          {hasSubmenu && popupMenu === item.label && (
            <div
              ref={popupRef}
              style={{
                position: 'absolute',
                top: 0,
                right: '60px',
                backgroundColor: '#34495e',
                borderRadius: '8px',
                padding: '8px 0',
                minWidth: '200px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                zIndex: 2000
              }}
            >
              {item.submenu?.map((subItem, subIndex) => (
                <div
                  key={`${index}-${subIndex}`}
                  onClick={() => {
                    navigate(subItem.path);
                    setPopupMenu(null);
                  }}
                  style={{
                    padding: '10px 15px',
                    cursor: 'pointer',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a5a'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span>{subItem.icon}</span>
                  <span>{subItem.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={index}>
        <div
          onClick={() => {
            if (hasSubmenu) {
              toggleMenu(item.label);
            } else {
              navigate(item.path);
            }
          }}
          style={menuItemStyle}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#34495e'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <span style={iconStyle}>{item.icon}</span>
          <span style={labelStyle}>{item.label}</span>
          {item.badge && <span style={{ backgroundColor: '#f44336', color: 'white', borderRadius: '10px', padding: '2px 6px', fontSize: '11px' }}>{item.badge}</span>}
          {hasSubmenu && (
            <span style={{ ...arrowStyle, transform: openMenus[item.label] ? 'rotate(90deg)' : 'rotate(0)' }}>
              ▶
            </span>
          )}
        </div>
        
        {hasSubmenu && openMenus[item.label] && (
          <div>
            {item.submenu?.map((subItem, subIndex) => (
              <div
                key={`${index}-${subIndex}`}
                onClick={() => navigate(subItem.path)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 15px 10px 45px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #3a4a5a',
                  color: '#ecf0f1',
                  fontSize: '13px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a5a'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={iconStyle}>{subItem.icon}</span>
                <span style={labelStyle}>{subItem.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={sidebarStyle}>
      <button onClick={handleToggle} style={toggleButtonStyle}>
        {isOpen ? '◀' : '☰'}
      </button>
      
      <div style={menuContainerStyle}>
        {menuItems.map((item, index) => renderMenuItem(item, index))}
      </div>

      {isOpen && user && (
        <button
          onClick={handleLogout}
          style={logoutButtonStyle}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d32f2f'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f44336'}
        >
          <span>🚪</span>
          تسجيل خروج
        </button>
      )}
    </div>
  );
}

export default SidebarNew;